import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../utils/api';

export default function LogsTab({ onStatsChange }) {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ status: '', template_name: '', to_address: '', date_from: '', date_to: '' });
  const [expandedId, setExpandedId] = useState(null);
  const perPage = 50;

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page, per_page: perPage });
      if (filters.status) params.set('status', filters.status);
      if (filters.template_name) params.set('template_name', filters.template_name);
      if (filters.to_address) params.set('to_address', filters.to_address);
      if (filters.date_from) params.set('date_from', filters.date_from);
      if (filters.date_to) params.set('date_to', filters.date_to);

      const res = await apiFetch(`/api/v1/admin/email-logs?${params}`);
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs || []);
        setTotal(data.total || 0);
      }
    } catch { /* ignore */ }
  }, [page, filters]);

  useEffect(() => { load(); }, [load]);

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const statusBadge = (status) => {
    const cls = {
      sent: 'em-badge-sent',
      delivered: 'em-badge-delivered',
      bounced: 'em-badge-bounced',
      complaint: 'em-badge-complaint',
      failed: 'em-badge-bounced',
    }[status] || '';
    return <span className={`em-badge ${cls}`}>{status}</span>;
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <div>
      {/* Filters */}
      <div className="em-filters">
        <select value={filters.status} onChange={e => updateFilter('status', e.target.value)}>
          <option value="">All Statuses</option>
          <option value="sent">Sent</option>
          <option value="delivered">Delivered</option>
          <option value="bounced">Bounced</option>
          <option value="complaint">Complaint</option>
          <option value="failed">Failed</option>
        </select>
        <input
          type="text"
          placeholder="Template name..."
          value={filters.template_name}
          onChange={e => updateFilter('template_name', e.target.value)}
        />
        <input
          type="text"
          placeholder="Recipient email..."
          value={filters.to_address}
          onChange={e => updateFilter('to_address', e.target.value)}
        />
        <input
          type="date"
          value={filters.date_from}
          onChange={e => updateFilter('date_from', e.target.value)}
          title="From date"
        />
        <input
          type="date"
          value={filters.date_to}
          onChange={e => updateFilter('date_to', e.target.value)}
          title="To date"
        />
        <button className="em-btn" onClick={() => { setFilters({ status: '', template_name: '', to_address: '', date_from: '', date_to: '' }); setPage(1); }}>
          Clear
        </button>
      </div>

      <table className="em-table">
        <thead>
          <tr>
            <th style={{ width: 30 }}></th>
            <th>Recipient</th>
            <th>Subject</th>
            <th>Template</th>
            <th>Status</th>
            <th>Date</th>
          </tr>
        </thead>
        <tbody>
          {logs.map(log => (
            <React.Fragment key={log.id}>
              <tr className={expandedId === log.id ? 'em-row-expanded' : ''} onClick={() => setExpandedId(expandedId === log.id ? null : log.id)} style={{ cursor: 'pointer' }}>
                <td>{expandedId === log.id ? '▾' : '▸'}</td>
                <td>{log.to_address}</td>
                <td>{log.subject}</td>
                <td>{log.template_name || '—'}</td>
                <td>{statusBadge(log.status)}</td>
                <td>{log.created_at ? new Date(log.created_at).toLocaleString() : '—'}</td>
              </tr>
              {expandedId === log.id && (
                <tr className="em-detail-row">
                  <td colSpan={6}>
                    <div className="em-log-detail">
                      <div><strong>ID:</strong> {log.id}</div>
                      <div><strong>SES Message ID:</strong> {log.ses_message_id || '—'}</div>
                      {log.error_message && <div><strong>Error:</strong> <span style={{ color: 'var(--danger)' }}>{log.error_message}</span></div>}
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
          {logs.length === 0 && (
            <tr><td colSpan={6} style={{ textAlign: 'center', padding: 30, color: 'var(--text-secondary)' }}>No email logs found</td></tr>
          )}
        </tbody>
      </table>

      {totalPages > 1 && (
        <div className="em-pagination">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
          <span>Page {page} of {totalPages} ({total} total)</span>
          <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
        </div>
      )}
    </div>
  );
}
